import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/Card'

interface FunnelRow {
  tenant_id: string
  project_id: string
  event: string
  count: number
  avg_ttfi_ms: number | null
}

interface PivotRow {
  tenant_id: string
  project_id: string
  upload_confirmed?: number
  recognition_card_shown?: number
  first_template_shown?: number
  first_result_viewed?: number
  misses_viewed?: number
  avg_ttfi_ms?: number | null
}

// EventKey enumerates exactly the five funnel event names — avoids 'as never' cast
type EventKey = 'upload_confirmed' | 'recognition_card_shown' | 'first_template_shown' | 'first_result_viewed' | 'misses_viewed'
const EVENT_KEYS = new Set<string>(['upload_confirmed', 'recognition_card_shown', 'first_template_shown', 'first_result_viewed', 'misses_viewed'])

function buildPivotRows(rows: FunnelRow[]): PivotRow[] {
  const map = new Map<string, PivotRow>()
  for (const row of rows) {
    const key = `${row.tenant_id}::${row.project_id}`
    if (!map.has(key)) {
      map.set(key, { tenant_id: row.tenant_id, project_id: row.project_id })
    }
    const pivot = map.get(key)!
    // Explicit per-event assignment — no 'as never' cast, fully type-safe
    if (EVENT_KEYS.has(row.event)) {
      const eventKey = row.event as EventKey
      if (eventKey === 'upload_confirmed') pivot.upload_confirmed = row.count
      else if (eventKey === 'recognition_card_shown') pivot.recognition_card_shown = row.count
      else if (eventKey === 'first_template_shown') pivot.first_template_shown = row.count
      else if (eventKey === 'first_result_viewed') pivot.first_result_viewed = row.count
      else if (eventKey === 'misses_viewed') pivot.misses_viewed = row.count
    }
    if (row.event === 'first_result_viewed' && row.avg_ttfi_ms != null) {
      pivot.avg_ttfi_ms = row.avg_ttfi_ms
    }
  }
  return Array.from(map.values())
}

export function AdminFunnelPage() {
  const [rows, setRows] = useState<FunnelRow[]>([])
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const token = localStorage.getItem('echo_session') ?? ''
    fetch('/api/v1/admin/funnel', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (r.status === 403) { setForbidden(true); return null }
        if (!r.ok) { setError('Could not load funnel data. Check your connection and reload.'); return null }
        return r.json() as Promise<{ rows: FunnelRow[] }>
      })
      .then(data => {
        if (data) setRows(data.rows ?? [])
      })
      .catch(() => setError('Could not load funnel data. Check your connection and reload.'))
      .finally(() => setLoading(false))
  }, [])

  if (forbidden) {
    return <div className="p-8 text-sm text-slate-400">Access restricted to owner role.</div>
  }

  if (loading) {
    return <div className="p-8 text-sm text-slate-400">Loading…</div>
  }

  if (error) {
    return <div className="p-8 text-sm text-red-500">{error}</div>
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <h1 className="text-xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>
          Funnel — Founder View
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Per-tenant funnel events. Internal only — owner role required.
        </p>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="funnel-admin-table">
            <thead>
              <tr>
                {['Tenant', 'Project', 'upload_confirmed', 'recognition_card_shown',
                  'first_template_shown', 'first_result_viewed', 'misses_viewed',
                  'Avg TTI (ms)'].map(col => (
                  <th key={col} className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide px-4 py-3 border-b border-border-gray whitespace-nowrap">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-sm text-slate-400 py-6 text-center px-4">
                    No funnel events recorded yet.
                  </td>
                </tr>
              ) : (
                buildPivotRows(rows).map((row, i) => (
                  <tr key={`${row.tenant_id}-${row.project_id}`} className={i % 2 === 0 ? '' : 'bg-soft-panel/20'}>
                    <td className="px-4 py-3 text-sm text-deep-ink border-b border-border-gray font-mono text-xs">{row.tenant_id.slice(0, 12)}…</td>
                    <td className="px-4 py-3 text-sm text-deep-ink border-b border-border-gray font-mono text-xs">{row.project_id.slice(0, 12)}…</td>
                    <td className="px-4 py-3 text-sm text-deep-ink border-b border-border-gray text-center">{row.upload_confirmed ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-deep-ink border-b border-border-gray text-center">{row.recognition_card_shown ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-deep-ink border-b border-border-gray text-center">{row.first_template_shown ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-deep-ink border-b border-border-gray text-center">{row.first_result_viewed ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-deep-ink border-b border-border-gray text-center">{row.misses_viewed ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-deep-ink border-b border-border-gray text-center">{row.avg_ttfi_ms != null ? Math.round(row.avg_ttfi_ms).toLocaleString() : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
